import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, DragEvent } from 'react'
import { fileSize, uploadClient, type UploadProject } from '../upload/uploadClient'
import { CHUNK_SIZE, startUpload, terminateUpload, type UploadHandle } from '../upload/tusUploader'
import {
  armReady,
  awaitingFile,
  canRemove,
  cancelActionFor,
  chunkProgress,
  forProject,
  otherProjects,
  makeEntry,
  matchAwaitingFile,
  needsFile,
  percentLabel,
  readyBytes,
  readyEntries,
  needsServerDelete,
  nextToStart,
  patchEntry,
  progressOf,
  queueSummary,
  removeEntry,
  type QueueEntry,
} from '../upload/queueModel'
import { projectToOpen, recallProject, rememberProject } from '../upload/selectionStore'
import {
  clearResumable,
  fileMatches,
  loadResumable,
  matchError,
  restoredEntry,
  saveResumable,
  type ResumeRecord,
} from '../upload/resumeStore'
import {
  forgetHandle,
  handlesFromDrop,
  pickFilesWithHandles,
  rememberHandle,
  resumeWithHandle,
  supportsFileHandles,
  type FileSystemFileHandleLike,
} from '../upload/fileHandles'
import { TextArea, TextField } from '@yoreflix/core/components/TextField'

/**
 * The provider's whole working surface (U3): projects on the left, the selected project's form and
 * files on the right — v1's shape, because film-makers understood it, in the admin design system.
 *
 * ★ The upload surface is the part that has to be genuinely good: this is a provider's first
 * encounter with our tech stack, and their connection is the thing most likely to fail.
 */

const wrap: CSSProperties = { flex: 1, display: 'flex', minHeight: 0 }
const leftPane: CSSProperties = {
  width: 280,
  borderRight: '1px solid var(--border)',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--sidebar)',
}
const paneHead: CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '10px 12px', borderBottom: '1px solid var(--border)', fontWeight: 600,
}
const listStyle: CSSProperties = { overflowY: 'auto', flex: 1, padding: 6 }
const rowStyle: CSSProperties = {
  padding: '8px 10px', borderRadius: 'var(--radius)', cursor: 'pointer', fontSize: 'var(--fs-sm)',
}
/**
 * The right-hand pane's gutter — and therefore the gap ABOVE the resume banner, which is the first
 * thing in the pane.
 *
 * ★ Named rather than repeated because the banner's bottom margin is deliberately the SAME value
 * (David, 2026-07-24): it sat flush against the "Project name" label below it, which read as part
 * of the form rather than a notice above it. Tying both to one constant is what stops the two gaps
 * drifting apart the next time the padding is touched.
 */
const PANE_PAD = 20
const rightPane: CSSProperties = { flex: 1, overflowY: 'auto', padding: PANE_PAD, minWidth: 0 }
const label: CSSProperties = { display: 'block', fontSize: 'var(--fs-sm)', fontWeight: 500, marginBottom: 5 }
const input: CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 'var(--fs-base)',
  fontFamily: 'var(--font-standard)', background: 'var(--input-bg)', color: 'var(--input-fg)',
  border: '1px solid var(--input)', borderRadius: 'var(--radius)', marginBottom: 12,
}
const panel: CSSProperties = {
  border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 16,
  background: 'var(--card)', marginTop: 18,
}
const btn: CSSProperties = {
  border: '1px solid var(--border)', color: 'var(--foreground)', borderRadius: 'var(--radius)',
  padding: '7px 12px', fontSize: 'var(--fs-sm)', cursor: 'pointer',
}
const cta: CSSProperties = {
  color: 'var(--primary-foreground)', border: '1px solid var(--primary)', borderRadius: 'var(--radius)',
  padding: '7px 14px', fontSize: 'var(--fs-sm)', fontWeight: 600, cursor: 'pointer',
}
const muted: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)' }
const tableHead: CSSProperties = {
  display: 'grid', gridTemplateColumns: '1fr 150px 110px', gap: 10, padding: '6px 8px',
  fontSize: 'var(--fs-xs)', color: 'var(--muted-foreground)', borderBottom: '1px solid var(--border)',
}
const tableRow: CSSProperties = { ...tableHead, fontSize: 'var(--fs-sm)', color: 'var(--foreground)' }
const scrim: CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
  display: 'grid', placeItems: 'center', padding: 24, zIndex: 50,
}
const dialog: CSSProperties = {
  width: 'min(420px, 100%)', background: 'var(--card)', color: 'var(--card-foreground)',
  border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 22,
  boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
}

let seq = 0
const nextId = (): string => `q${Date.now().toString(36)}-${seq++}`

export function ProjectsScreen() {
  const [projects, setProjects] = useState<UploadProject[]>([])
  const [selected, setSelected] = useState<string>('')
  const [linked, setLinked] = useState(true)
  const [loading, setLoading] = useState(true)
  const [queue, setQueue] = useState<QueueEntry[]>([])
  const [dragging, setDragging] = useState(false)
  const [resume, setResume] = useState<ResumeRecord[]>([])
  const [resumeError, setResumeError] = useState<string | null>(null)
  const [resumeNotice, setResumeNotice] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<UploadProject | null>(null)
  const [confirmCancel, setConfirmCancel] = useState<ResumeRecord | null>(null)
  const [confirmTransfer, setConfirmTransfer] = useState<QueueEntry | null>(null)

  const handles = useRef(new Map<string, UploadHandle>())
  const fileInput = useRef<HTMLInputElement | null>(null)
  const resumeInput = useRef<HTMLInputElement | null>(null)
  const restoreInput = useRef<HTMLInputElement | null>(null)
  const rowInput = useRef<HTMLInputElement | null>(null)
  const pendingResume = useRef<ResumeRecord | null>(null)
  const pendingRow = useRef<QueueEntry | null>(null)
  // ⚠ tus fires onProgress very often; re-rendering on every event makes a long upload janky.
  const lastPaint = useRef(new Map<string, number>())

  const reload = useCallback(async () => {
    const res = await uploadClient.projects()
    setLinked(res.Linked)
    setProjects(res.Projects ?? [])
    setLoading(false)
    return res.Projects ?? []
  }, [])

  useEffect(() => {
    /*
      ★★ SELECT THE PROJECT THE RESTORED QUEUE BELONGS TO, not simply the newest one.

      Projects come back newest-first, so `list[0]` is whatever was created last — which after a
      reload is very often NOT the project the queued files were dropped into. Landing on the wrong
      one is how a perfectly good upload came to look lost: the transfers were displayed under a
      project that never received them (David, 2026-07-24).
    */
    void reload().then((list) => {
      if (list.length === 0) return
      // Work in progress first, then where they last were, then the newest — see projectToOpen.
      const waiting = loadResumable().find((r) => r.url === undefined)?.projectKey ?? ''
      setSelected((s) => s || projectToOpen(list, waiting, recallProject()))
    })

    /*
      ★★ RESTORE THE WHOLE QUEUE, not just the file that was in flight (David, 2026-07-24).
      Refreshing mid-batch used to leave the provider with one banner and no idea what else had
      been waiting behind it — on a ten-reel delivery that is ten files to find again from memory.

      The split is simply whether the record has a tus url:
        · WITH one  → bytes exist on the server, so it belongs to the "Unfinished upload" banner
                      and re-picking continues from the server's offset.
        · WITHOUT   → it never started, so it goes straight back into the queue in its old place,
                      waiting for its file (see queueModel.needsFile).
    */
    const stored = loadResumable()
    const restored = stored.filter((r) => r.url === undefined).map(restoredEntry)
    setResume(stored.filter((r) => r.url !== undefined))
    setQueue(restored)
  }, [reload])

  // ⚠⚠ BOTH ARGUMENTS. Passing only the queue removed every banner record the moment this screen
  // mounted with an empty queue — see the note on saveResumable. An interrupted upload then
  // survived exactly one reload.
  useEffect(() => { saveResumable(queue, resume) }, [queue, resume])

  // ★ Remembered so the next visit opens where they left off — a pointer only, re-validated on
  // load against the projects the server actually returns.
  useEffect(() => { if (selected !== '') rememberProject(selected) }, [selected])

  const project = projects.find((p) => p.PrimaryKey === selected) ?? null
  // ★ The Transfers panel speaks only about the project on screen — see queueModel.forProject.
  const projectQueue = forProject(queue, selected)
  const elsewhere = otherProjects(queue, selected)

  // ── the queue pump: one upload at a time (the RequestToken rotation forbids more) ──────────
  useEffect(() => {
    const next = nextToStart(queue)
    if (!next) return

    const entry = next
    setQueue((q) => patchEntry(q, entry.id, { state: 'uploading' }))

    const record = resume.find((r) => r.id === entry.id)
    const handle = startUpload({
      file: entry.file as File,
      projectKey: entry.projectKey,
      resumeUrl: entry.url ?? record?.url,
      onUrl: (url) => setQueue((q) => patchEntry(q, entry.id, { url })),
      onProgress: (sent) => {
        const now = Date.now()
        if (now - (lastPaint.current.get(entry.id) ?? 0) < 200) return
        lastPaint.current.set(entry.id, now)
        setQueue((q) => patchEntry(q, entry.id, { sent }))
      },
      onSuccess: (fileKey) => {
        setQueue((q) => patchEntry(q, entry.id, { state: 'complete', sent: entry.size, fileKey }))
        clearResumable(entry.id)
        void reload()
      },
      onError: (err) => {
        setQueue((q) => patchEntry(q, entry.id, { state: 'failed', error: err.message }))
      },
    })
    handles.current.set(entry.id, handle)
    // Intentionally keyed on the queue only — `resume`/`reload` are stable enough and re-running
    // this on their identity would restart an in-flight upload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue, reload])

  /**
   * @param handles optional, positionally matched to `files` — present only on browsers with the
   *   File System Access API, where remembering them lets a reload resume with one click instead
   *   of a re-pick.
   */
  const enqueue = useCallback(
    (files: FileList | File[], projectKey: string, handles?: (FileSystemFileHandleLike | null)[]) => {
      const list = Array.from(files)
      if (list.length === 0) return
      const entries = list.map((f) => makeEntry(f, projectKey, nextId()))
      entries.forEach((entry, i) => {
        const handle = handles?.[i]
        if (handle) void rememberHandle(entry.id, handle)
      })
      setQueue((q) => [...q, ...entries])
    },
    [],
  )

  /** The picker, handle-aware where the browser allows it and an ordinary file input otherwise. */
  async function chooseFiles(projectKey: string) {
    if (supportsFileHandles()) {
      const picked = await pickFilesWithHandles()
      if (picked.length > 0) {
        enqueue(picked.map((p) => p.file), projectKey, picked.map((p) => p.handle))
      }
      return
    }
    fileInput.current?.click()
  }

  /**
   * ★★ ASK BEFORE THROWING AWAY BYTES (David, 2026-07-24: *"the cancel button needs a confirmation
   * to avoid accidental click"*).
   *
   * ★ BUT ONLY WHEN THERE IS SOMETHING TO LOSE. `needsServerDelete` is exactly that question — has
   * this entry got bytes on the server? A **Ready** file that has never started has none, and
   * removing it is the cheap, reversible act that explicit Start exists to make possible; putting a
   * dialog in front of it would tax the safe path to protect the expensive one. Same for a restored
   * row waiting to be re-picked, and for failed clutter.
   *
   * ⇒ So the dialog appears on a transfer that is actually mid-flight, which is the misclick that
   * costs a provider hours.
   */
  function askToRemove(entry: QueueEntry) {
    if (!canRemove(entry)) return
    if (!needsServerDelete(entry)) {
      void removeFromQueue(entry)
      return
    }
    setConfirmTransfer(entry)
  }

  async function removeFromQueue(entry: QueueEntry) {
    if (!canRemove(entry)) return
    setConfirmTransfer(null)
    const handle = handles.current.get(entry.id)
    const hadServerRow = needsServerDelete(entry)

    // ★★ See cancelActionFor: a live handle is not the only way bytes can exist on the server, and
    // assuming it was stranded a real 32MB part-file on live.
    switch (cancelActionFor(entry, handle !== undefined)) {
      case 'abort-handle':
        // Terminates on the server too — a cancelled transfer must not leave bytes awaiting a
        // collection that will never come.
        try { await handle?.abort() } catch { /* the row goes either way */ }
        break
      case 'terminate-url':
        // No live handle: restored after a reload, or queued behind another upload and never
        // started in this session. The URL is all that is needed to throw the bytes away.
        try { await terminateUpload(entry.url as string) } catch { /* as above */ }
        break
      case 'nothing':
        break
    }
    handles.current.delete(entry.id)
    clearResumable(entry.id)
    setQueue((q) => removeEntry(q, entry.id))

    // ⚠⚠ AND REFETCH THE PROJECT (David, 2026-07-24). An upload that has reached the server owns a
    // row in "Uploaded files" showing "In progress" — the server drops it the moment the tus DELETE
    // lands, but this screen holds the projects it fetched, so without this the cancelled file goes
    // on advertising itself as still arriving until something else happens to reload. Skipped when
    // there was no server row (a `ready` entry has never been created) — there is nothing to catch
    // up on and a needless round trip would flicker the list.
    if (hadServerRow) void reload()
  }

  async function createProject() {
    const res = await uploadClient.createProject('New project')
    const list = await reload()
    const key = res.Project?.PrimaryKey ?? list[0]?.PrimaryKey ?? ''
    setSelected(key)
  }

  /**
   * ★★ SAVES AS YOU TYPE (debounced), exactly as v1 did.
   *
   * An earlier cut saved on BLUR only, which is a quiet regression: type a paragraph into Notes,
   * close the tab without clicking elsewhere, and it is gone. Providers used v1 for years with
   * `input` + a 1s debounce and reasonably expect their typing to stick.
   *
   * ⚠ Blur still FLUSHES immediately rather than waiting out the timer — clicking straight from
   * the field to another project must not race the pending save.
   */
  const saveTimers = useRef(new Map<string, number>())

  async function saveField(key: string, patch: Record<string, string>, refresh = true) {
    await uploadClient.updateProject(key, patch)
    // ⚠ Only refresh the list on a flush. Re-fetching on every debounced keystroke makes the
    // left-hand list flicker while someone is mid-word, for no benefit — the field they are
    // typing in is already the truth.
    if (refresh) void reload()
  }

  function scheduleSave(projectKey: string, field: string, value: string) {
    const id = `${projectKey}:${field}`
    const pending = saveTimers.current.get(id)
    if (pending !== undefined) window.clearTimeout(pending)
    saveTimers.current.set(
      id,
      window.setTimeout(() => {
        saveTimers.current.delete(id)
        void saveField(projectKey, { [field]: value }, false)
      }, 900),
    )
  }

  function flushSave(projectKey: string, field: string, value: string) {
    const id = `${projectKey}:${field}`
    const pending = saveTimers.current.get(id)
    if (pending !== undefined) {
      window.clearTimeout(pending)
      saveTimers.current.delete(id)
    }
    void saveField(projectKey, { [field]: value })
  }

  /**
   * Has this project got uploads we have accepted? If so it cannot be deleted (B7) — and the
   * button is not rendered at all (David, 2026-07-24), rather than offered and then refused.
   *
   * ⚠ Counts COMPLETE files only, matching the server's rule exactly. An in-flight upload can
   * still be cancelled, and once it is, the project becomes deletable again — so the button
   * reappearing is correct, not a glitch.
   * ⚠ A client-side convenience: the server remains the authority, and its refusal is still
   * handled in deleteProject() in case this view is stale.
   */
  const completedFiles = (p: UploadProject): number => p.Files.filter((f) => f.Status === 'complete').length

  /** Clicking Delete does not delete — it asks first (David, 2026-07-24). */
  function askToDelete(p: UploadProject) {
    setNotice(null)
    setConfirmDelete(p)
  }

  async function deleteProject(key: string) {
    const res = await uploadClient.deleteProject(key)
    if (!res.Deleted) {
      // ⚠ Not an error — a deliberate refusal (B7). Say why, in the provider's terms.
      setNotice(
        res.Reason === 'has_completed_files'
          ? `This project can’t be deleted — it holds ${res.CompletedFiles ?? 0} uploaded file(s). Those are with us now; contact us if something needs removing.`
          : 'This project could not be deleted.',
      )
      return
    }
    setNotice(null)
    setConfirmDelete(null)

    /*
      ⚠⚠ ITS TRANSFERS GO WITH IT. The server deletes a project's files as it goes (the cascade),
      so any queue row still pointing at this project is now an orphan: re-picking it would create
      an upload against a project that no longer exists and fail with a 404 the provider can do
      nothing about.

      ★ This became worth fixing the moment the queue started surviving a reload. Before that an
      orphaned row disappeared at the next refresh and nobody ever saw it; now it would sit there
      permanently, offering a file that can never be sent.
    */
    for (const entry of queue.filter((e) => e.projectKey === key)) {
      handles.current.delete(entry.id)
      void forgetHandle(entry.id)
    }
    setQueue((q) => q.filter((e) => e.projectKey !== key))
    setResume((r) => r.filter((x) => x.projectKey !== key))

    const list = await reload()
    setSelected(list[0]?.PrimaryKey ?? '')
  }

  // ── resume ────────────────────────────────────────────────────────────────────────────────
  /**
   * Try the stored file handle first (one permission click, no dialog); fall back to asking them
   * to re-pick, which is the universal path and the only one Safari has.
   *
   * ⚠ Called straight from a click so `requestPermission()` still has its user gesture.
   */
  async function askForResumeFile(record: ResumeRecord) {
    setResumeError(null)

    if (supportsFileHandles()) {
      const file = await resumeWithHandle(record.id)
      if (file && file.name === record.name && file.size === record.size) {
        setResume((r) => r.filter((x) => x.id !== record.id))
        setQueue((q) => [...q, { ...makeEntry(file, record.projectKey, record.id), url: record.url, state: 'queued' }])
        return
      }
      // Handle gone, permission refused, or the file changed on disk — nothing is wrong, we just
      // ask the ordinary way.
      void forgetHandle(record.id)
    }

    pendingResume.current = record
    resumeInput.current?.click()
  }

  function onResumePicked(files: FileList | null) {
    const record = pendingResume.current
    const file = files?.[0]
    if (!record || !file) return
    const problem = matchError(record, file)
    if (problem) { setResumeError(problem); return }

    setResumeError(null)
    setResume((r) => r.filter((x) => x.id !== record.id))
    // ⚠ A resume skips the Start button: picking the interrupted file IS the deliberate action,
    // and asking twice would be pedantic.
    setQueue((q) => [
      ...q,
      { ...makeEntry(file, record.projectKey, record.id), url: record.url, state: 'queued' },
    ])
  }

  /**
   * ★★ HAND BACK **ONE NAMED ROW'S** FILE (David, 2026-07-24: *"we need a repick button for each
   * row otherwise its confusing"*).
   *
   * ★ WHY THE BATCH BUTTON WAS NOT ENOUGH, and he was right: it offers "all 2 files at once" and
   * opens one dialog, but a provider is free to select only one of them — and then nothing on the
   * dialog told them WHICH files it was still waiting for. A button on the row removes the
   * ambiguity entirely: the file it wants is the one named beside it.
   *
   * ⚠ Errors land ON THE ROW (`error`), not in the banner at the top of the screen. The banner is
   * about the interrupted upload; a mis-pick here belongs next to the row that refused it, which
   * may be several rows down.
   */
  async function askForRowFile(entry: QueueEntry) {
    setQueue((q) => patchEntry(q, entry.id, { error: undefined }))

    // Same courtesy as the batch path: on Chromium a stored handle means one permission click and
    // no dialog at all. Everywhere else — Safari included — this is skipped entirely.
    if (supportsFileHandles()) {
      const file = await resumeWithHandle(entry.id)
      if (file && fileMatches(entry, file)) {
        setQueue((q) => patchEntry(q, entry.id, { file }))
        return
      }
      void forgetHandle(entry.id)
    }

    pendingRow.current = entry
    rowInput.current?.click()
  }

  function onRowFilePicked(files: FileList | null) {
    const entry = pendingRow.current
    const file = files?.[0]
    if (!entry || !file) return

    // ⚠ Name AND size, exactly as a resume is checked — a row is a slot for one specific file, and
    // filling it with a different cut of the same title would upload something nobody asked for.
    if (!fileMatches(entry, file)) {
      const problem = file.name !== entry.name
        ? `That is a different file — this row is waiting for “${entry.name}”.`
        : 'That file is a different size to the one that was waiting.'
      setQueue((q) => patchEntry(q, entry.id, { error: problem }))
      return
    }

    setQueue((q) => patchEntry(q, entry.id, { file, error: undefined }))
  }

  /**
   * ★★ HAND THE FILES BACK TO A RESTORED QUEUE (David, 2026-07-24).
   *
   * ★ ONE GESTURE FOR THE WHOLE BATCH. Ten restored rows must not mean ten file dialogs, so this
   * opens a single multi-select picker and matches each chosen file to whichever row was waiting
   * for it — by name AND size, the same rule a resume uses. Files that match nothing are reported
   * rather than silently ignored.
   *
   * ★ It also satisfies the BANNER: someone selecting everything in Finder will include the file
   * that was mid-flight, and refusing it there while a separate button asks for the same file would
   * be perverse. A banner match keeps its tus url, so it continues from the server's offset rather
   * than starting over.
   *
   * ⚠ On Chromium the stored file HANDLES are tried first, which restores those rows with a single
   * permission click and no dialog at all. Anything the handles cannot supply falls through to the
   * picker — the universal path, and the only one Mac Safari has.
   * ⚠ Called straight from a click: `requestPermission()` has no effect without a user gesture.
   */
  async function restoreQueueFiles() {
    setResumeError(null)
    setResumeNotice(null)

    // ⚠ Scoped to the project on screen, like the button that calls it — bulk must not reach into
    // transfers the provider cannot see from here.
    const pending = awaitingFile(forProject(queue, selected))

    if (supportsFileHandles()) {
      const recovered: { entry: QueueEntry; file: File }[] = []
      for (const entry of pending) {
        const file = await resumeWithHandle(entry.id)
        // ⚠ Same name+size guard as a re-pick. A handle can outlive an edit to the file it points at.
        if (file && fileMatches(entry, file)) recovered.push({ entry, file })
      }
      if (recovered.length > 0) {
        setQueue((q) => recovered.reduce((acc, r) => patchEntry(acc, r.entry.id, { file: r.file }), q))
      }

      /*
        ⚠⚠ IF THE HANDLES COVERED EVERY WAITING ROW, WE ARE DONE — DO NOT OPEN A DIALOG.

        This used to also require `resume.length === 0`, so the mere existence of an unfinished-upload
        BANNER record forced a file dialog after a bulk restore that had already succeeded. David hit
        exactly that: *"the file picker dialog was presented and i cancelled it but the files still
        seem to be restored"* — a dialog asking for files it did not need, and cancelling it (rightly)
        changed nothing.

        The banner is a separate surface with its own button; bulk re-select answers for the QUEUE.
        (A dialog opened from here still accepts a banner file if one is selected — see
        onRestorePicked — but it is no longer opened on the banner's behalf.)
      */
      if (recovered.length === pending.length) {
        setResumeNotice(`Picked up ${recovered.length} file${recovered.length === 1 ? '' : 's'} where you left off.`)
        return
      }
    }

    restoreInput.current?.click()
  }

  /**
   * Match a multi-selection back onto whatever was waiting — queue rows first, then the banner.
   *
   * ⚠ Reports the misses. A provider who picks the wrong folder would otherwise see a dialog close
   * and nothing at all change, which is indistinguishable from the feature being broken.
   */
  function onRestorePicked(files: FileList | null) {
    const picked = Array.from(files ?? [])
    if (picked.length === 0) return

    const attached: QueueEntry[] = []
    const resumed: ResumeRecord[] = []
    const unmatched: string[] = []

    // ⚠ Worked out against a LOCAL copy that shrinks as matches are made, so two identically named
    // files of the same size cannot both claim the same waiting row.
    let waiting = awaitingFile(queue)
    let banner = resume

    for (const file of picked) {
      const entry = matchAwaitingFile(waiting, file)
      if (entry) {
        attached.push({ ...entry, file })
        waiting = waiting.filter((e) => e.id !== entry.id)
        continue
      }
      const record = banner.find((r) => fileMatches(r, file))
      if (record) {
        resumed.push(record)
        banner = banner.filter((r) => r.id !== record.id)
        setQueue((q) => [...q, { ...makeEntry(file, record.projectKey, record.id), url: record.url, state: 'queued' }])
        continue
      }
      unmatched.push(file.name)
    }

    if (attached.length > 0) {
      setQueue((q) => attached.reduce((acc, e) => patchEntry(acc, e.id, { file: e.file }), q))
    }
    if (resumed.length > 0) {
      setResume((r) => r.filter((x) => !resumed.some((used) => used.id === x.id)))
    }

    const restored = attached.length + resumed.length
    if (restored > 0) {
      setResumeNotice(`Picked up ${restored} file${restored === 1 ? '' : 's'} where you left off.`)
    }
    if (unmatched.length > 0) {
      setResumeError(
        `Nothing was waiting for ${unmatched.map((n) => `“${n}”`).join(', ')} — check it is the same file, or drop it in as a new upload.`,
      )
    }
  }

  /**
   * ★★ THROW AWAY AN INTERRUPTED UPLOAD (David, 2026-07-24).
   *
   * Until now the banner offered exactly one way out — re-pick the file and let it finish. So a
   * provider who dropped the wrong file, or simply changed their mind about a multi-GB master, was
   * stuck looking at the offer on every visit, and its half-written bytes stayed on our box
   * indefinitely: an incomplete upload is deliberately NEVER counted as stale by the admin alarm
   * (U4), so nothing downstream would ever flag it either.
   *
   * ⚠ THIS ONE CONFIRMS, unlike the Cancel beside a running transfer. The two buttons sit side by
   * side here — one carries on, the other throws away what may be hours of transfer — and the bytes
   * cannot be got back: a terminated upload starts again from zero.
   *
   * ⚠ The record is cleared only once the SERVER has answered. Clearing it first would look tidy
   * and leave orphaned bytes with nothing left pointing at them.
   */
  async function cancelResume(record: ResumeRecord) {
    setResumeError(null)
    setResumeNotice(null)
    setConfirmCancel(null)

    // ⚠ No url means nothing was ever created server-side (a queue row restored after a reload),
    // so there is nothing to terminate — forget it locally and say so. The banner only ever shows
    // records that DO have one, but the types no longer guarantee it and a DELETE to `undefined`
    // would be a confusing failure rather than a no-op.
    const outcome = record.url === undefined ? 'gone' : await terminateUpload(record.url)
    if (outcome === 'failed') {
      setResumeError('That upload couldn’t be cancelled just now. Check your connection and try again — nothing has changed.')
      return
    }

    void forgetHandle(record.id)
    clearResumable(record.id)
    setResume((r) => r.filter((x) => x.id !== record.id))
    // ⚠⚠ ALWAYS refetch (David, 2026-07-24) — a part-sent upload has a row in "Uploaded files"
    // reading "In progress", and cancelling it must take that row away with it. The server has
    // already dropped it; this screen is the only thing still showing it.
    void reload()

    // ⚠ 'complete' is NOT a failure to report as one: the upload had actually finished and only the
    // browser missed the last response. Saying "cancelled" would be a lie about a file we hold.
    if (outcome === 'complete') {
      setResumeNotice(`“${record.name}” had already finished uploading, so there was nothing to cancel — it’s with us.`)
      return
    }
    setResumeNotice(`“${record.name}” was cancelled. Nothing was kept, so uploading it again will start from the beginning.`)
  }

  /**
   * ★★ THE DEPTH COUNTER — why drag highlighting needs one.
   *
   * `dragenter`/`dragleave` fire for EVERY element the pointer crosses, including children of the
   * drop zone. So moving from the box onto the text inside it raises a `dragleave` for the box,
   * even though the cursor never left. Toggling state on that alone flickers violently.
   *
   * Counting depth fixes it: enter increments, leave decrements, and the zone is "active" only
   * while the count is above zero. The first enter and the true final leave are the only two
   * transitions the UI ever sees.
   *
   * ⚠ Reset to 0 on drop — a drop consumes the drag without a matching `dragleave`, so a counter
   * that only ever decremented on leave would drift positive and leave the zone stuck highlighted.
   */
  const dragDepth = useRef(0)

  function onDragEnter(e: DragEvent) {
    e.preventDefault()
    dragDepth.current += 1
    if (dragDepth.current === 1) setDragging(true)
  }

  function onDragLeave() {
    dragDepth.current = Math.max(0, dragDepth.current - 1)
    if (dragDepth.current === 0) setDragging(false)
  }

  function onDrop(e: DragEvent) {
    e.preventDefault()
    dragDepth.current = 0
    setDragging(false)
    if (!project) return

    // ⚠ Read BOTH synchronously: a DataTransfer is neutered the moment this handler yields, so
    // grabbing `files` after an await would find it empty.
    const files = e.dataTransfer.files
    const key = project.PrimaryKey
    if (!supportsFileHandles()) {
      enqueue(files, key)
      return
    }
    const pending = handlesFromDrop(e.dataTransfer)
    void pending.then((handles) => enqueue(files, key, handles)).catch(() => enqueue(files, key))
  }

  if (loading) return <div style={{ ...muted, padding: 24 }}>Loading…</div>

  if (!linked) {
    return (
      <div style={{ padding: 24, maxWidth: 520 }}>
        <h2 style={{ fontSize: 'var(--fs-heading)', margin: '0 0 8px' }}>Account not linked</h2>
        <p style={muted}>
          Your account isn’t linked to an organisation yet, so there’s nowhere to put your uploads.
          Email <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>info@yoreflix.com</a> and
          we’ll set it up.
        </p>
      </div>
    )
  }

  return (
    <div style={wrap}>
      <aside style={leftPane}>
        <div style={paneHead}>
          <span>Projects</span>
          <button className="admin-btn" style={btn} onClick={() => void createProject()}>+ New</button>
        </div>
        <div style={listStyle}>
          {projects.length === 0 && <p style={{ ...muted, padding: 8 }}>No projects yet.</p>}
          {projects.map((p) => (
            <div
              key={p.PrimaryKey}
              className="admin-option"
              style={rowStyle}
              data-active={p.PrimaryKey === selected ? '' : undefined}
              onClick={() => setSelected(p.PrimaryKey)}
            >
              {p.Name || '(untitled)'}
              <div style={{ ...muted, fontSize: 'var(--fs-xs)' }}>
                {p.Files.length} file{p.Files.length === 1 ? '' : 's'}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <main style={rightPane}>
        {/*
          An interrupted upload, offered back on the next visit.

          ⚠ The panel also stays up when the last record has GONE but there is something to say
          about it ("cancelled", "had already finished") — a banner that vanishes at the same moment
          as the click leaves the provider unsure whether anything happened at all.
          ⚠ `marginBottom: PANE_PAD` matches the pane's own gutter above it (David) — see PANE_PAD.
        */}
        {(resume.length > 0 || resumeNotice || resumeError) && (
          <div style={{
            ...panel, marginTop: 0, marginBottom: PANE_PAD,
            borderColor: 'var(--accent2-base)', background: 'var(--accent2-hint)',
          }}>
            {resume.length > 0 && <strong style={{ fontSize: 'var(--fs-sm)' }}>Unfinished upload</strong>}
            {resume.map((r) => (
              <div key={r.id} style={{ marginTop: 8, fontSize: 'var(--fs-sm)' }}>
                <div>“{r.name}” didn’t finish. Re-select the same file and it will carry on from where it stopped.</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button className="admin-btn" style={btn} onClick={() => void askForResumeFile(r)}>
                    Re-select “{r.name}”
                  </button>
                  {/* ⚠ Marked as the destructive one: the neighbouring button carries the upload on,
                      and the two must not look interchangeable. It still asks before acting. */}
                  <button
                    className="admin-btn-danger"
                    style={{ ...btn, borderColor: 'var(--destructive)', color: 'var(--destructive-dark)' }}
                    onClick={() => setConfirmCancel(r)}
                  >
                    Cancel upload
                  </button>
                </div>
              </div>
            ))}
            {resumeNotice && (
              <div style={{ fontSize: 'var(--fs-sm)', marginTop: resume.length > 0 ? 12 : 0 }}>{resumeNotice}</div>
            )}
            {resumeError && <div style={{ color: 'var(--destructive-dark)', fontSize: 'var(--fs-sm)', marginTop: 8 }}>{resumeError}</div>}
          </div>
        )}

        {!project && <p style={muted}>Select a project, or create one.</p>}

        {project && (
          <>
            <label style={label} htmlFor="pname">Project name</label>
            <TextField
              id="pname" style={input} defaultValue={project.Name} key={`n${project.PrimaryKey}`}
              onChange={(e) => scheduleSave(project.PrimaryKey, 'Name', e.target.value)}
              onBlur={(e) => flushSave(project.PrimaryKey, 'Name', e.target.value)}
            />
            <label style={label} htmlFor="pdesc">Description</label>
            <TextField
              id="pdesc" style={input} defaultValue={project.Description} key={`d${project.PrimaryKey}`}
              onChange={(e) => scheduleSave(project.PrimaryKey, 'Description', e.target.value)}
              onBlur={(e) => flushSave(project.PrimaryKey, 'Description', e.target.value)}
            />
            <label style={label} htmlFor="pnotes">Notes</label>
            <TextArea
              id="pnotes" style={{ ...input, minHeight: 70 }} defaultValue={project.Notes} key={`o${project.PrimaryKey}`}
              onChange={(e) => scheduleSave(project.PrimaryKey, 'Notes', e.target.value)}
              onBlur={(e) => flushSave(project.PrimaryKey, 'Notes', e.target.value)}
            />

            {notice && (
              <div style={{ ...panel, marginTop: 0, borderColor: 'var(--accent2-base)', background: 'var(--accent2-hint)', fontSize: 'var(--fs-sm)' }}>
                {notice}
              </div>
            )}

            {/* ── the upload surface ───────────────────────────────────────────── */}
            <div
              style={{
                ...panel,
                borderStyle: 'dashed',
                borderColor: dragging ? 'var(--primary)' : 'var(--border)',
                background: dragging ? 'var(--nav-hover)' : 'var(--card)',
                textAlign: 'center',
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDrop={onDrop}
            >
              {/*
                ⚠⚠ THIS CONTENT MUST NOT CHANGE WHILE DRAGGING. It used to swap the wording and hide
                the button, which meant entering the zone deleted an element from under the cursor,
                shifted the layout, fired more enter/leave events, flipped the state back and put the
                button back — a self-sustaining flicker. Only colour changes now; the box never
                resizes and nothing is added or removed.
                ★ `pointerEvents: none` on the children is the other half: with it, the browser
                cannot raise dragleave for a child at all, so the depth counter stays honest.
              */}
              <div style={{ pointerEvents: dragging ? 'none' : undefined }}>
                <p style={{ margin: '0 0 10px', fontSize: 'var(--fs-sm)' }}>
                  Drag files here to upload, or
                </p>
                <button className="admin-btn-primary" style={cta} onClick={() => void chooseFiles(project.PrimaryKey)}>
                  Select file(s)
                </button>
                <p style={{ ...muted, fontSize: 'var(--fs-xs)', margin: '10px 0 0' }}>
                  Large files are fine. If your connection drops, the upload picks up where it left off.
                </p>
              </div>
              <input
                ref={fileInput} type="file" multiple hidden
                onChange={(e) => { enqueue(e.target.files ?? [], project.PrimaryKey); e.target.value = '' }}
              />
            </div>

            {(projectQueue.length > 0 || elsewhere.count > 0) && (
              <div style={panel}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                  <strong style={{ fontSize: 'var(--fs-sm)' }}>Transfers</strong>
                  <span style={muted}>{queueSummary(projectQueue)}</span>
                </div>

                {/*
                  ⚠ NOTHING IS SILENTLY OMITTED. Scoping the list to this project is what stops a
                  transfer appearing under a heading it does not belong to — but a queue that simply
                  shrank would be its own alarm, so anything queued elsewhere is counted, named and
                  reachable in one click.
                */}
                {elsewhere.count > 0 && (
                  <div style={{ ...muted, fontSize: 'var(--fs-xs)', marginBottom: 12 }}>
                    {elsewhere.count} file{elsewhere.count === 1 ? '' : 's'} queued under another project
                    {elsewhere.uploading > 0 ? ` (${elsewhere.uploading} uploading)` : ''}.{' '}
                    <button
                      className="admin-btn"
                      style={{ ...btn, padding: '2px 8px', fontSize: 'var(--fs-xs)' }}
                      onClick={() => setSelected(elsewhere.firstProjectKey)}
                    >
                      Show {projects.find((x) => x.PrimaryKey === elsewhere.firstProjectKey)?.Name || 'them'}
                    </button>
                  </div>
                )}

                {/*
                  ★★ NOTHING IS SENT UNTIL THIS IS PRESSED. Files land "ready" so a mis-drop costs
                  nothing — which matters here more than in an ordinary uploader, because a
                  COMPLETED upload is permanent to the provider (B7) and an accidental one cannot be
                  undone by the person who made the mistake.
                  ★ Whole batch only (David) — a per-file start would be a second control for a
                  case nobody has, and the queue is sequential anyway.
                */}
                {/*
                  ★★ THE RESTORED QUEUE'S ONE ACTION. It sits ABOVE Start deliberately: while any
                  row is still missing its bytes, finding those files is the only thing the provider
                  can usefully do, and Start would skip straight past them.

                  ⚠⚠ THE BULK BUTTON EXISTS ONLY WHERE IT CAN ACTUALLY DO THE JOB (David,
                  2026-07-24: *"i'm not sure we should have the 'restore all' because if the files
                  are from different folders it's not possible"*, then *"this bulk select should be
                  completely hidden if not Chrome with FSA API"*). Both calls are right, and the
                  reason is the MECHANISM, not the browser:

                    · WITH File System Access: no dialog at all. Stored handles reopen every file
                      wherever it lives, so one click really does take the whole batch across any
                      number of folders. Worth a button.
                    · WITHOUT it (Safari, Firefox): the only tool available is an OS open dialog,
                      which multi-selects inside ONE folder. A "do them all" button there promises
                      something the operating system will not deliver — a provider whose reels sit
                      in separate folders would press it, get a partial result, and be left working
                      out why. The per-row button is the honest answer, so it is the ONLY one shown.

                  ★ THE EXPLANATION STAYS ON BOTH. It is the sentence that tells someone why a
                  reload left them holding a list of files the browser cannot read; only the BUTTON
                  is gated.

                  ⚠ A label here must not read like the row's button. An earlier cut said "Re-select
                  files…", one letter and an ellipsis from "Re-select file" — caught by a Playwright
                  selector matching THREE buttons where two were expected, i.e. substring matching
                  spotting a design smell before a human would.
                */}
                {awaitingFile(projectQueue).length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
                    {supportsFileHandles() && (
                      <button className="admin-btn-primary" style={cta} onClick={() => void restoreQueueFiles()}>
                        Bulk re-select {awaitingFile(projectQueue).length} file
                        {awaitingFile(projectQueue).length === 1 ? '' : 's'}
                      </button>
                    )}
                    <span style={{ ...muted, fontSize: 'var(--fs-xs)' }}>
                      {supportsFileHandles()
                        ? 'Refreshing the page keeps your list, but your browser cannot re-read the files themselves. Re-select them all here, or one at a time on each row.'
                        : 'Refreshing the page keeps your list, but your browser cannot re-read the files themselves — use Re-select file on each row to hand them back.'}
                    </span>
                  </div>
                )}

                {readyEntries(projectQueue).length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                    <button
                      className="admin-btn-primary"
                      style={cta}
                      onClick={() => setQueue((q) => armReady(q, selected))}
                    >
                      Start transfer — {readyEntries(projectQueue).length} file
                      {readyEntries(projectQueue).length === 1 ? '' : 's'}, {fileSize(readyBytes(projectQueue))}
                    </button>
                    <span style={{ ...muted, fontSize: 'var(--fs-xs)' }}>
                      Nothing is sent until you press this.
                    </span>
                  </div>
                )}
                {projectQueue.map((e) => {
                  const chunks = chunkProgress(e, CHUNK_SIZE)
                  return (
                  <div key={e.id} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 'var(--fs-sm)' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.name}
                        {/* ★ Size on every transfer row: on a multi-GB master it is the difference
                            between "this is taking ages" and "of course it is, it is 4.72 Gb". */}
                        <span style={{ ...muted, marginLeft: 8, fontSize: 'var(--fs-xs)' }}>{fileSize(e.size)}</span>
                      </span>
                      <span style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
                        {/* ⚠ `needsFile` is tested FIRST and overrides the state: a restored row
                            may legitimately be `queued` (they pressed Start before the refresh),
                            and showing it as "Waiting" would blame the queue for a wait that is
                            actually on the provider to end. */}
                        <span style={muted}>
                          {needsFile(e) ? 'Needs re-selecting'
                            : e.state === 'complete' ? 'Done'
                            : e.state === 'failed' ? 'Failed'
                            : e.state === 'ready' ? 'Ready'
                            : e.state === 'queued' ? 'Waiting'
                            : percentLabel(e, CHUNK_SIZE)}
                        </span>
                        {/*
                          ★ THE ROW'S OWN "Re-select file" (David). The batch button can be given any
                          subset of the files it asks for, which left no way to see WHICH row was
                          still waiting. This one is unambiguous: the file it wants is named
                          immediately to its left.
                        */}
                        {needsFile(e) && (
                          <button
                            className="admin-btn-primary"
                            style={{ ...cta, padding: '3px 10px' }}
                            onClick={() => void askForRowFile(e)}
                          >
                            Re-select file
                          </button>
                        )}
                        {/* ⚠ No button at all once complete — the server would refuse it (B7). */}
                        {canRemove(e) && (
                          <button className="admin-btn" style={{ ...btn, padding: '3px 8px' }} onClick={() => askToRemove(e)}>
                            {e.state === 'uploading' ? 'Cancel' : 'Remove'}
                          </button>
                        )}
                      </span>
                    </div>
                    <div style={{ height: 4, background: 'var(--muted)', borderRadius: 2, marginTop: 5 }}>
                      <div style={{
                        height: '100%', width: `${progressOf(e) * 100}%`, borderRadius: 2,
                        background: e.state === 'failed' ? 'var(--destructive)' : 'var(--primary)',
                        transition: 'width 200ms linear',
                      }} />
                    </div>
                    {/*
                      ★★ THE FAST-MOVING READOUT. Under the bar rather than beside the percentage:
                      the row header is already name + size + status + button, and a multi-GB
                      transfer is precisely when the row must not start wrapping.
                      ⚠ Only appears while uploading and only for files of more than one chunk —
                      chunkProgress() returns null otherwise.
                    */}
                    {chunks && (
                      <div style={{ ...muted, fontSize: 'var(--fs-xs)', marginTop: 4 }}>
                        Transferring chunk {chunks.current} of {chunks.total}
                      </div>
                    )}
                    {e.error && <div style={{ color: 'var(--destructive-dark)', fontSize: 'var(--fs-xs)', marginTop: 4 }}>{e.error}</div>}
                  </div>
                  )
                })}
              </div>
            )}

            <div style={panel}>
              <strong style={{ fontSize: 'var(--fs-sm)' }}>Uploaded files</strong>
              {project.Files.length === 0 && <p style={{ ...muted, marginBottom: 0 }}>Nothing uploaded to this project yet.</p>}
              {project.Files.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <div style={tableHead}><span>File name</span><span>Completed</span><span>Size</span></div>
                  {project.Files.map((f) => (
                    <div key={f.PrimaryKey} style={tableRow}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.Name}</span>
                      <span style={muted}>
                        {f.CompletedDtInt ? new Date(f.CompletedDtInt * 1000).toLocaleDateString() : 'In progress'}
                      </span>
                      <span style={muted}>{fileSize(f.SizeBytes)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/*
              ⚠ HIDDEN, not disabled and not refused-on-click, once anything has been accepted:
              those files are a handover and the project can never be removed by the provider, so
              offering the button at all would only advertise something that cannot happen.
            */}
            {completedFiles(project) === 0 && (
              <button
                className="admin-btn-danger"
                style={{ ...btn, marginTop: 16, borderColor: 'var(--destructive)', color: 'var(--destructive-dark)' }}
                onClick={() => askToDelete(project)}
              >
                Delete project
              </button>
            )}
          </>
        )}

        <input ref={resumeInput} type="file" hidden onChange={(e) => { onResumePicked(e.target.files); e.target.value = '' }} />
        {/* ⚠ `multiple` — the whole point of the restore flow is ONE dialog for the whole batch. */}
        <input
          ref={restoreInput} type="file" multiple hidden
          onChange={(e) => { onRestorePicked(e.target.files); e.target.value = '' }}
        />
        {/* ⚠ Single-file, unlike the batch input above: this one fills one named row. */}
        <input
          ref={rowInput} type="file" hidden
          onChange={(e) => { onRowFilePicked(e.target.files); e.target.value = '' }}
        />

        {/*
          Destructive confirmation. ⚠ The SAFE action takes focus and Escape cancels, so the
          reflexive responses (hit Enter, hit Escape) both leave the project intact — a dialog
          where the destructive button is the default is worse than no dialog at all.
        */}
        {confirmDelete && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-title"
            style={scrim}
            onClick={() => setConfirmDelete(null)}
            onKeyDown={(e) => { if (e.key === 'Escape') setConfirmDelete(null) }}
          >
            <div style={dialog} onClick={(e) => e.stopPropagation()}>
              <h2 id="confirm-delete-title" style={{ fontSize: 'var(--fs-heading)', fontWeight: 600, margin: '0 0 6px' }}>
                Delete this project?
              </h2>
              <p style={{ ...muted, margin: '0 0 18px' }}>
                “{confirmDelete.Name || '(untitled)'}” will be removed, along with its description
                and notes. This can’t be undone.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="admin-btn"
                  style={btn}
                  autoFocus
                  onClick={() => setConfirmDelete(null)}
                >
                  Cancel
                </button>
                <button
                  className="admin-btn-danger"
                  style={{ ...btn, borderColor: 'var(--destructive)', color: 'var(--destructive-dark)' }}
                  onClick={() => void deleteProject(confirmDelete.PrimaryKey)}
                >
                  Delete project
                </button>
              </div>
            </div>
          </div>
        )}

        {/*
          Cancelling a transfer that is ACTUALLY RUNNING — the misclick that costs hours.
          ⚠ Only reachable when bytes exist server-side (see askToRemove); removing a Ready file is
          still one click, because nothing is lost.
          ★ Unlike the resume banner's dialog, this one CAN quote real progress: `sent` is live here,
          where a stored record only knows the file's total size.
        */}
        {confirmTransfer && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-transfer-title"
            style={scrim}
            onClick={() => setConfirmTransfer(null)}
            onKeyDown={(e) => { if (e.key === 'Escape') setConfirmTransfer(null) }}
          >
            <div style={dialog} onClick={(e) => e.stopPropagation()}>
              <h2 id="confirm-transfer-title" style={{ fontSize: 'var(--fs-heading)', fontWeight: 600, margin: '0 0 6px' }}>
                Cancel this transfer?
              </h2>
              <p style={{ ...muted, margin: '0 0 18px' }}>
                “{confirmTransfer.name}” is about {percentLabel(confirmTransfer, CHUNK_SIZE)} uploaded. Cancelling
                throws away what has arrived so far, and sending it again will start from the
                beginning.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="admin-btn" style={btn} autoFocus onClick={() => setConfirmTransfer(null)}>
                  Keep uploading
                </button>
                <button
                  className="admin-btn-danger"
                  style={{ ...btn, borderColor: 'var(--destructive)', color: 'var(--destructive-dark)' }}
                  onClick={() => void removeFromQueue(confirmTransfer)}
                >
                  Cancel transfer
                </button>
              </div>
            </div>
          </div>
        )}

        {/*
          Same contract as the delete dialog: the SAFE action holds focus and Escape cancels.
          ⚠ The safe button says "Keep it", not "Cancel" — in a dialog about cancelling an upload a
          button labelled "Cancel" means both things at once, and the reader has to guess.
        */}
        {confirmCancel && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-cancel-title"
            style={scrim}
            onClick={() => setConfirmCancel(null)}
            onKeyDown={(e) => { if (e.key === 'Escape') setConfirmCancel(null) }}
          >
            <div style={dialog} onClick={(e) => e.stopPropagation()}>
              <h2 id="confirm-cancel-title" style={{ fontSize: 'var(--fs-heading)', fontWeight: 600, margin: '0 0 6px' }}>
                Cancel this upload?
              </h2>
              {/*
                ⚠ NO BYTE FIGURE HERE. The stored record carries the file's SIZE, not how much of it
                arrived — quoting it would tell someone we already hold 4.72 Gb of a file we have
                300 Mb of. The only honest number lives on the server, and one HEAD request is not
                worth making them wait for it to read a warning.
              */}
              <p style={{ ...muted, margin: '0 0 18px' }}>
                “{confirmCancel.name}” will be thrown away, along with the part of it we’ve already
                received. If you upload it later it will start again from the beginning.
              </p>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="admin-btn" style={btn} autoFocus onClick={() => setConfirmCancel(null)}>
                  Keep it
                </button>
                <button
                  className="admin-btn-danger"
                  style={{ ...btn, borderColor: 'var(--destructive)', color: 'var(--destructive-dark)' }}
                  onClick={() => void cancelResume(confirmCancel)}
                >
                  Cancel upload
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
